import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';

export default function AffiliateTerms() {
  return (
    <div className="min-h-screen bg-white" data-testid="affiliate-terms">
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2"><img src="/images/area-logo.png" alt="AREA" className="h-10" /></Link>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600">
            <Link to="/affiliate" className="hover:text-[#1e3a5f]">Affiliate</Link>
            <Link to="/login" className="hover:text-[#1e3a5f]">Sign In</Link>
          </div>
          <Link to="/register"><Button className="bg-[#1e3a5f] hover:bg-[#152d4a] text-white px-6">Join AREA</Button></Link>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="text-3xl sm:text-4xl font-bold text-[#0f1f33] mb-2">Affiliate Program Terms</h1>
        <p className="text-sm text-gray-400 mb-10">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

        <div className="prose prose-gray max-w-none text-sm leading-relaxed space-y-8">
          <section>
            <h2 className="text-lg font-semibold text-[#0f1f33] mb-3">1. Program Overview</h2>
            <p className="text-gray-600">The American Real Estate Alliance ("AREA") Affiliate Program allows enrolled members ("Partners") to earn commissions by referring new members to AREA. All AREA members, regardless of membership tier (Free, Pro, or Founder), are eligible to participate in the affiliate program.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#0f1f33] mb-3">2. Lifetime Commissions</h2>
            <p className="text-gray-600">AREA offers lifetime commissions to Partners. This means that after a referred member's first qualifying paid conversion, the Partner's attribution is permanently locked. The Partner will continue to earn commissions on all future qualifying payments made by that referred member, including renewals and upgrades, for the lifetime of the referred member's AREA membership.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#0f1f33] mb-3">3. Attribution Rules</h2>
            <ul className="list-disc pl-5 text-gray-600 space-y-2">
              <li><strong>Referral Window:</strong> A 60-day attribution window applies from the date of the initial referral click. If the referred visitor does not create an account within this window, the referral expires.</li>
              <li><strong>Last-Click Attribution:</strong> Before a referred member's first paid conversion, last-click attribution applies. If a visitor clicks multiple affiliate links, the most recent click determines attribution.</li>
              <li><strong>Attribution Lock:</strong> After the first qualifying paid conversion (upgrade to Pro or Founder), the Partner's attribution is locked permanently. No subsequent affiliate clicks will change the attribution.</li>
              <li><strong>Admin Override:</strong> AREA administrators reserve the right to manually reassign attribution in cases of fraud, error, or at their sole discretion.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#0f1f33] mb-3">4. Commission Rates</h2>
            <p className="text-gray-600 mb-3">Commission rates are determined by the membership tier sold and/or the Partner type, as configured by AREA administration. Default rates are:</p>
            <div className="bg-gray-50 rounded-xl p-5 space-y-2">
              <p className="text-gray-600"><strong>By Tier Sold:</strong> Free — 20% | Pro — 25% | Founder — 30%</p>
              <p className="text-gray-600"><strong>By Partner Type:</strong> Affiliate — 20% | Creator Partner — 25% | Strategic Partner — 30%</p>
            </div>
            <p className="text-gray-600 mt-3">Commissions are calculated on net eligible membership revenue. AREA reserves the right to modify commission rates prospectively with reasonable notice.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#0f1f33] mb-3">5. Payout Terms</h2>
            <ul className="list-disc pl-5 text-gray-600 space-y-2">
              <li><strong>Payout Threshold:</strong> The minimum payout threshold is $200 USD. Commissions below this threshold will accumulate until the threshold is met.</li>
              <li><strong>Bank Account:</strong> Partners must provide valid bank account information for direct deposit before receiving payouts.</li>
              <li><strong>Tax Information:</strong> Partners must submit required tax information (e.g., W-9 for US Partners) before payout eligibility.</li>
              <li><strong>Hold Period:</strong> New commissions are held for a 14-day review period before becoming eligible for payout.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#0f1f33] mb-3">6. Clawbacks and Reversals</h2>
            <p className="text-gray-600">AREA reserves the right to reverse commissions in the following cases:</p>
            <ul className="list-disc pl-5 text-gray-600 space-y-2">
              <li>Refunds issued to referred members</li>
              <li>Chargebacks filed by referred members</li>
              <li>Fraudulent or manipulated referrals</li>
              <li>Self-referral violations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#0f1f33] mb-3">7. Prohibited Conduct</h2>
            <ul className="list-disc pl-5 text-gray-600 space-y-2">
              <li><strong>Self-Referrals:</strong> Partners may not refer themselves, their own accounts, or accounts under their control.</li>
              <li><strong>Fraud:</strong> Any attempt to manipulate clicks, signups, or conversions through bots, fake accounts, or deceptive practices is strictly prohibited.</li>
              <li><strong>Misrepresentation:</strong> Partners must not misrepresent AREA, its services, pricing, or the affiliate program in any promotional material.</li>
              <li><strong>Spam:</strong> Unsolicited bulk messaging or spamming of affiliate links is prohibited.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#0f1f33] mb-3">8. Termination</h2>
            <p className="text-gray-600">AREA may suspend or terminate a Partner's participation in the affiliate program at any time for violation of these terms, fraudulent activity, or at AREA's sole discretion. Upon termination, unpaid approved commissions will be paid out according to the standard payout schedule, unless the termination is due to fraud.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#0f1f33] mb-3">9. Program Modifications</h2>
            <p className="text-gray-600">AREA reserves the right to modify these terms, commission rates, payout thresholds, and program rules at any time. Changes will be communicated to Partners and will apply prospectively. Continued participation in the program after notice of changes constitutes acceptance of the updated terms.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#0f1f33] mb-3">10. Revenue Basis</h2>
            <p className="text-gray-600">All commissions are calculated on net eligible membership revenue. This is defined as the actual amount received by AREA after payment processing fees, refunds, and chargebacks. AREA reserves the right to define which revenue events qualify for commission generation.</p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-100 text-center">
          <p className="text-gray-500 text-sm mb-6">By participating in the AREA Affiliate Program, you agree to these terms.</p>
          <Link to="/affiliate">
            <Button className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white px-8">
              Back to Affiliate Program <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
